import React from "react";
import { Pencil, Trash2, Lock } from "lucide-react";
import { GF_DEF, comprobacion } from "@/lib/gastoFijo";

const mx = (v) => { const r = Math.round(v); return `${r < 0 ? "-" : ""}$${Math.abs(r).toLocaleString("es-MX")}`; };
const fecha = (iso, o) => new Date(`${iso}T12:00:00`).toLocaleDateString("es-MX", o);
const mesTxt = (e) => fecha(e.fin_mes, { month: "long", year: "numeric" });
const th = "px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-center leading-tight";
const sepRow = (f, cols) => (f.titulo
  ? <tr key={f.titulo}><td colSpan={cols} className="px-3 pt-3 pb-1 text-xs font-bold text-slate-700">{f.titulo}</td></tr>
  : <tr key={`s${f.sep}`}><td colSpan={cols} className="h-3" /></tr>);

function Encabezado({ calc, canEdit, onEdit, onDelete, puede, bloque }) {
  return (
    <thead>
      <tr className="bg-slate-50">
        <th className="px-3 py-2" />
        {calc.map((e, i) => (
          <th key={e.id} colSpan={3} className="px-3 py-2 border-l-4 border-white" data-testid={`gf-col-${i + 1}`}>
            <div className="flex items-center justify-center gap-2 text-xs text-slate-700">
              Ejercicio {i + 1}
              {(i < calc.length - 1 || !puede(e)) && <Lock className="w-3.5 h-3.5 text-slate-400" title="Ejercicio no editable" data-testid={`gf-lock-${i + 1}`} />}
              {canEdit && i === calc.length - 1 && puede(e) && <button onClick={() => onEdit(e)} data-testid={`gf-edit-${i + 1}`} className="p-1 rounded hover:bg-slate-200"><Pencil className="w-3.5 h-3.5" /></button>}
              {canEdit && i === calc.length - 1 && puede(e) && <button onClick={() => onDelete(e)} data-testid={`gf-delete-${i + 1}`} className="p-1 rounded text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
          </th>
        ))}
      </tr>
      <tr className="bg-[var(--gf-head)] text-white">
        <th className={`${th} text-left`}>{bloque}</th>
        {calc.map((e) => ["Presupuesto mensual", "Aplicación diaria", "Aplicación semanal"].map((h, j) => (
          <th key={`${e.id}${h}`} className={`${th} ${j === 0 ? "border-l-4 border-white" : ""}`}>{h}</th>
        )))}
      </tr>
    </thead>
  );
}

const Info = ({ label, calc, render, testid }) => (
  <tr className="border-t border-slate-200">
    <td className="px-3 py-2 text-xs font-semibold text-slate-600 whitespace-nowrap">{label}</td>
    {calc.map((e, i) => <td key={e.id} colSpan={3} className="px-3 py-2 text-center font-bold text-[var(--gf-info-text)] bg-[var(--gf-info)] border-l-4 border-white" data-testid={`${testid}-${i + 1}`}>{render(e)}</td>)}
  </tr>
);

export function GastoFijoTabla({ calc, canEdit, onEdit, onDelete, puede = () => true, def = GF_DEF }) {
  const cols = 1 + calc.length * 3;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="gf-table">
        <Encabezado calc={calc} canEdit={canEdit} onEdit={onEdit} onDelete={onDelete} puede={puede} bloque={def.bloque} />
        <tbody>
          {def.filas.map((f) => (!f.k ? sepRow(f, cols) : (
            <tr key={f.k} className={`border-t border-slate-100 ${f.bold ? "font-bold" : ""} ${f.total ? "bg-[var(--gf-total)]" : ""}`} data-testid={`gf-row-${f.k}`}>
              <td className="px-3 py-1.5 text-slate-900 whitespace-nowrap">{f.label}</td>
              {calc.map((e, i) => ["mensual", "diaria", "semanal"].map((t, j) => (
                <td key={`${e.id}${t}`} className={`px-3 py-1.5 text-right tabular-nums text-slate-900 ${j === 0 ? "border-l-4 border-white" : ""}`} data-testid={`gf-${f.k}-${t}-${i + 1}`}>{mx(e[t][f.k])}</td>
              )))}
            </tr>
          )))}
          <tr><td colSpan={cols} className="h-3" /></tr>
          <Info label="MES DE EJERCICIO" calc={calc} testid="gf-mes" render={mesTxt} />
          <Info label="FECHA DE REGISTRO" calc={calc} testid="gf-fecha-reg" render={(e) => fecha(e.fecha_registro, { day: "numeric", month: "long", year: "numeric" })} />
          <Info label="DÍAS DE APLICACIÓN" calc={calc} testid="gf-dias" render={(e) => e.dias} />
        </tbody>
      </table>
    </div>
  );
}

export function GastoFijoComprobacion({ calc, def = GF_DEF }) {
  const c = comprobacion(calc, def);
  const tramoTxt = (t) => {
    const ini = new Date(`${t.e.inicio}T12:00:00`);
    const fin = new Date(`${t.siguiente || t.e.fin_mes}T12:00:00`);
    if (t.siguiente) fin.setDate(fin.getDate() - 1);
    return `${ini.getDate()} al ${fin.getDate()} ${fin.toLocaleDateString("es-MX", { month: "long" })}`;
  };
  const hdr = "px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-center bg-[var(--gf-comp)] text-slate-900";
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="gf-comprobacion">
      <p className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-700">Comprobación</p>
      <table className="w-full text-sm">
        <thead><tr><th className="px-3 py-2" />{c.tramos.map((t, i) => <th key={t.e.id} className={hdr} data-testid={`gf-tramo-${i + 1}`}>{tramoTxt(t)}</th>)}<th className={hdr}>Acumulado</th></tr></thead>
        <tbody>
          {def.filas.map((f) => (!f.k ? sepRow(f, c.tramos.length + 2) : (
            <tr key={f.k} className={`border-t border-slate-100 ${f.bold ? "font-bold" : ""}`}>
              <td className="px-3 py-1.5 text-slate-900 whitespace-nowrap">{f.label}</td>
              {c.filas[f.k].vals.map((v, i) => <td key={i} className="px-3 py-1.5 text-right tabular-nums">{mx(v)}</td>)}
              <td className="px-3 py-1.5 text-right tabular-nums" data-testid={`gf-acum-${f.k}`}>{mx(c.filas[f.k].acum)}</td>
            </tr>
          )))}
          <tr className="border-t border-slate-200 font-bold bg-[var(--gf-comp-soft)]">
            <td className="px-3 py-2 text-xs">DÍAS DE APLICACIÓN</td>
            {c.tramos.map((t) => <td key={t.e.id} className="px-3 py-2 text-center">{t.dias}</td>)}
            <td className="px-3 py-2 text-center" data-testid="gf-dias-total">{c.diasTotal}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
